// src/game/tutorial.ts — Eğitim modu ders makinesi (docs/TUTORIAL.md §3 ders listesi + §5 geri bildirim + §6 tablo birebir).
// NEDEN saf: bu dosya yalnız core/config okur; three.js, DOM ve Audio İMPORT ETMEZ. Panel (view) ve kukla denetleyicisi (game)
// buradan yalnız DURUM okur; ses için `onCue` geri çağrısı verilir — böylece ders mantığı DOM'suz test edilebilir.
// Akış: presenter her sim tick'inde `onTick(world)`, her sim olayında `onEvent(e, world)` çağırır.
import { Ev, FState } from '../core/types.ts';
import type { SimEvent, SimWorld } from '../core/types.ts';
import { NO_ID, NO_TICK } from '../config/constants.ts';
import { MOVE_ID } from '../config/moves.ts';
import { T } from '../config/tuning.ts';

/** Kuklanın ders boyunca ne yapacağı (TUTORIAL §2). */
export type DummyMode = 'idle' | 'attacks' | 'fights';

/** Panelin/ses katmanının duyacağı işaretler (TUTORIAL §5). presenter: progress→ui('select'), complete→ui('start'),
 *  advance→ui('move'), back→ui('back'), skip→ui('start'). */
export type TutorialCue = 'progress' | 'complete' | 'advance' | 'back' | 'skip';

/** Tuş rozetleri: klavye ve pad ayrı (panelde pad bağlıysa pad rozeti önce gösterilir). */
export interface TutorialKeys { kb: readonly string[]; pad: readonly string[] }

export interface TutorialLesson {
  id: string;
  title: string;
  /** Panel açıklaması (en çok 3 satır) — belgeden birebir. */
  text: string;
  /** Hedef satırı. */
  goal: string;
  keys: TutorialKeys;
  /** Sayacın hedefi; 0 = bitmeyen ders (serbest antrenman). */
  need: number;
  dummy: DummyMode;
  /** Takılınca beliren ipucu satırı. */
  hint: string;
  /** Ders sonu bilgisi (varsa). */
  note?: string;
}

/** Eğitime özgü süreler/eşikler. NEDEN burada: tuning.ts sim sayılarının tek yeri; bunlar sim'i etkilemeyen sunum/pedagoji
 *  sayıları ve yalnız bu modül tarafından kullanılıyor (sim'e sızmaz). Süreler tick (60 Hz). */
export const TUT = {
  advanceDelay: 90,        // ders tamamlandıktan sonra otomatik geçiş beklemesi (1.5 s)
  hintDelay: 480,          // son ilerlemeden bu yana ipucu eşiği (8 s)
  hintPulseDelay: 720,     // ipucunun bir kez daha yanıp sönmesi (12 s)
  idleHintDelay: 1800,     // serbest antrenmanda hareketsizlik ipucu (30 s)
  walkSpeed: 0.3,          // "yürüyor" sayılan yer hızı
  walkTicks: 60,           // 1. derste yürüme/koşma için kesintisiz süre
  damageGoal: 40,          // 6. ders: mankenin sayısı
  hitWindow: 60,           // 9. ders: knockdown'ı oyuncuya bağlayan yedek pencere
  dodgeWindow: 30,         // 13. ders: kuklanın hamlesinden sonra sıyrılmanın sayıldığı pencere (0.5 s)
} as const;

/**
 * Girdi adının iki karşılığı: pad bağlıysa "çubuk", klavyede "yön tuşları". Metinler yer tutucu yazar, sözcüğü panel
 * doldurur — çünkü hangi girdinin bağlı olduğunu yalnız panel bilir (bkz. `fillInput`).
 * NEDEN sözcük değil ek'li biçim: Türkçe'de ek girdi adına göre değişiyor ("çubuğu" ↔ "yön tuşlarını"); tek bir yalın
 * ada sonradan ek çakmak yanlış sonuç verirdi. Bir vaka (koşma ipucu) öbek: klavyede analog "hafif itme" YOK, o cümle
 * iki girdide farklı şey söylemek zorunda.
 */
export const INPUT_WORDS: Readonly<Record<string, { readonly kb: string; readonly pad: string }>> = {
  yon: { kb: 'yön tuşları', pad: 'çubuk' },
  yonu: { kb: 'yön tuşlarını', pad: 'çubuğu' },
  yona: { kb: 'yön tuşlarına', pad: 'çubuğa' },
  yonla: { kb: 'yön tuşlarıyla', pad: 'çubukla' },
  yonun: { kb: 'yön tuşlarının', pad: 'çubuğun' },
  /** 1. dersin koşma ipucu: pad'de "sonuna kadar it", klavyede tek hız rampası var → "bırakma". */
  tamGaz: { kb: 'yön tuşunu bırakma', pad: 'çubuğu sonuna kadar it' },
};

/** Yer tutucu: `{yonu}` (küçük) ya da `{Yonu}` (cümle başı → ilk harf Türkçe kuralıyla büyür). */
const PLACEHOLDER = /\{([A-Za-zçğıöşüÇĞİÖŞÜ]+)\}/g;

/**
 * Ders metnindeki girdi yer tutucularını doldurur — SÖZLEŞME: panel (view) metni ekrana yazmadan önce
 * `fillInput(text, padOn)` çağırır (`text`, `goal`, `hint`, `note` ve övgü için aynı işlev), pad takılıp çıkarılınca
 * görünen metinleri yeniden yazar. Mimari tek yönlü olduğu için (view, game'i içe aktaramaz) panel bu işlevi
 * DIŞARIDAN alır: `new TutorialHud(container, fillInput)`.
 * Bilinmeyen anahtar olduğu gibi bırakılır: sessizce yanlış sözcük yazmaktansa eksik eşleme göze çarpsın.
 */
export function fillInput(text: string, padOn: boolean): string {
  if (!text.includes('{')) return text;
  return text.replace(PLACEHOLDER, (all: string, key: string): string => {
    const lower = key.charAt(0).toLocaleLowerCase('tr') + key.slice(1);
    const w = INPUT_WORDS[lower];
    if (!w) return all;
    const v = padOn ? w.pad : w.kb;
    return key === lower ? v : v.charAt(0).toLocaleUpperCase('tr') + v.slice(1);
  });
}

/** Ders tamamlanınca sırayla dönen kısa övgüler (TUTORIAL §5). */
export const PRAISE = ['Harika!', 'Aynen böyle!', 'Şimdi anladın.', 'Tam istediğim gibi.'] as const;

/** 24 ders — metinler docs/TUTORIAL.md §3'ten birebir. */
export const LESSONS: readonly TutorialLesson[] = [
  {
    id: 'yuru-kos', title: 'Yürü ve Koş',
    // Oyunun amacı ilk cümlede söylenir: öğrenci neyi neden öğrendiğini baştan bilsin (oyun testi bulgusu).
    text: 'Bu oyunda amaç rakibi platformdan aşağı atmak. Önce yürümeyi öğrenelim. {Yonla} bir yöne git: önce yürürsün, sonra hızlanıp koşarsın.',
    goal: 'Biraz yürü, sonra koş.',
    keys: { kb: ['W', 'A', 'S', 'D'], pad: ['Sol çubuk'] }, need: 2, dummy: 'idle',
    hint: '{TamGaz} — biraz sonra koşmaya başlarsın.',
    note: 'Koşarken bazı hamlelerin değişir. Onları sonra göstereceğim.',
  },
  {
    id: 'zipla', title: 'Zıplama',
    text: 'Zıplamak kaçmanın en kolay yolu. Havadayken sana ulaşmak zordur. Üç kez zıpla, hissini al.',
    goal: '3 kez zıpla.',
    keys: { kb: ['Space'], pad: ['A'] }, need: 3, dummy: 'idle',
    hint: 'Tuşu kısa bas: kısa zıplarsın. Basılı tut: daha yükseğe çıkarsın.',
  },
  {
    id: 'cift-zipla', title: 'Çift Zıplama',
    text: 'Havadayken bir kez daha zıplayabilirsin. Bu senin can simidin: kenardan düşerken seni geri getirir. Her havalanışta bir hakkın var.',
    goal: 'Havadayken 3 kez tekrar zıpla.',
    keys: { kb: ['Space'], pad: ['A'] }, need: 3, dummy: 'idle',
    hint: 'Yere değmeden, havadayken tekrar bas.',
    note: 'Yere değince hakkın yenilenir.',
  },
  {
    id: 'ilk-yumruk', title: 'İlk Yumruk',
    text: 'Şimdi mankene yaklaş ve yumruk at. Hafif yumruk hızlıdır, çabuk toparlanırsın. Üç kez dene.',
    goal: 'Mankene 3 kez yumruk at.',
    keys: { kb: ['J', 'Sol tık'], pad: ['X'] }, need: 3, dummy: 'idle',
    hint: 'Biraz daha yaklaş; kolun yetişmiyor.',
  },
  {
    id: 'uclu-seri', title: 'Üçlü Seri',
    text: 'Aynı tuşa arka arkaya basarsan üç farklı yumruk çıkar: hızlı, sert, sonra havaya kaldıran. Ritmi yakala, acele etme.',
    goal: 'Üçlü seriyi 2 kez tamamla.',
    keys: { kb: ['J', 'J', 'J'], pad: ['X', 'X', 'X'] }, need: 2, dummy: 'idle',
    hint: 'İlk yumruk değdikten hemen sonra bas — çok erken basarsan seri başa döner.',
    note: 'Üçüncü yumruk mankeni biraz havalandırır. Bu senin fırsat anın.',
  },
  {
    id: 'hasar-yuzdesi', title: 'Yüzde Ne Demek',
    text: 'Mankenin kafasının üstündeki sayıya bak. Her vuruşta büyüyor. Sayı büyüdükçe vurduğun kişi daha uzağa savrulur — canı azalmaz, hafifler.',
    goal: 'Mankenin sayısını %40’a çıkar.',
    keys: { kb: ['J'], pad: ['X'] }, need: TUT.damageGoal, dummy: 'idle',
    hint: 'Vurmaya devam et, sayı yükselecek.',
    note: 'Sayı beyazdan sarıya, sonra kırmızıya döner. Kırmızı manken tek vuruşla arenadan çıkabilir.',
  },
  {
    id: 'tekme', title: 'Tekme',
    text: 'Tekme yumruktan yavaştır ama çok daha serttir. İnsanı iter. Kenara doğru itmek istediğinde bunu kullan.',
    goal: 'Mankene 3 kez tekme at.',
    keys: { kb: ['K', 'Sağ tık'], pad: ['Y'] }, need: 3, dummy: 'idle',
    hint: 'Tekme yavaş başlar; mankenin yanına gel, sonra bas.',
    note: 'Tuşu basılı tutup ıskalarsan kollarını savurarak dönersin. Onu da deneyebilirsin.',
  },
  {
    id: 'sersemlik-halkasi', title: 'Sersemleme',
    text: 'Sayının etrafındaki halkayı gördün mü? Vurdukça doluyor. Yeterince dolunca bir ! işareti çıkar: o an manken sersemlemiştir ve yere serilebilir.',
    goal: 'Mankende 2 kez ! işaretini çıkar.',
    keys: { kb: ['J', 'J', 'J'], pad: ['X', 'X', 'X'] }, need: 2, dummy: 'idle',
    hint: 'Ara vermeden vur — beklersen halka boşalır.',
    note: 'Halka kendi kendine iner. Yani ! çıktığında hemen davranmalısın.',
  },
  {
    id: 'yere-serme', title: 'Yere Serme',
    text: 'Şimdi işin güzel kısmı. Üç yumruğu at, hemen ardından tekmeyi ekle. Manken sersemken gelen tekme onu yere serer.',
    goal: 'Mankeni 2 kez yere ser.',
    keys: { kb: ['J', 'J', 'J', 'K'], pad: ['X', 'X', 'X', 'Y'] }, need: 2, dummy: 'idle',
    hint: 'Üçüncü yumruk değer değmez tekme tuşuna bas.',
    note: 'Dördüncü tuş her zaman tekme olmak zorunda değil. Bir sonraki derste tutma tuşunu deneyeceğiz.',
  },
  {
    id: 'yerden-kaldir', title: 'Yerdekini Kaldır',
    // Köprü cümlesi: kukla bu derse yatarak başlıyor (tutorialMode), oyuncu neden yerde olduğunu bilsin.
    text: 'Az önce onu yere serdin, şimdi yerde yatıyor. Yerden kaldıralım: tam yanına git ve tutma tuşuna bas. Onu omzuna alırsın.',
    goal: 'Yatan mankeni 2 kez omzuna al.',
    keys: { kb: ['L', 'E'], pad: ['B'] }, need: 2, dummy: 'idle',
    hint: 'Tam yanına gel — bir adım uzaktan tutamazsın.',
    note: 'Kaldırırken elin boş olmalı. Silah tutuyorsan kaldıramazsın.',
  },
  {
    id: 'tasi-ve-firlat', title: 'Taşı ve Fırlat',
    // Aynı köprü: kukla bu derse de yatarak başlar, önce onu omzuna alması gerekiyor.
    text: 'Manken yine yerde; önce onu omzuna al. Omzunda taşırken yavaş yürürsün ama istediğin yere götürebilirsin. Fırlatmak için {yonla} atmak istediğin yöne dön ve tutma tuşuna tekrar bas.',
    goal: 'Mankeni 2 kez omzundan fırlat.',
    keys: { kb: ['L', 'E'], pad: ['B'] }, need: 2, dummy: 'idle',
    hint: '{Yonla} atmak istediğin yöne dön, sonra tutma tuşuna bas.',
    note: 'Omzundakini uzun süre tutamazsın; bir süre sonra kendi kendine bırakırsın.',
  },
  {
    id: 'ayakta-tut', title: 'Ayaktakini Tut',
    text: 'Ayakta duran birini de tutabilirsin. Tutma bloğu deler: karşındaki korunuyor olsa bile yakalarsın. Tut, sonra fırlat.',
    goal: 'Önce tut (1), sonra fırlat (2).',
    keys: { kb: ['L', 'E'], pad: ['B'] }, need: 2, dummy: 'idle',
    hint: 'Tuttuktan sonra aynı tuşa bir daha bas — fırlatırsın.',
    note: 'Basmadan beklersen onu omzuna alırsın. Tutulan kişi tuşlara basarak kurtulabilir, unutma.',
  },
  {
    id: 'dodge', title: 'Sıyrılma',
    text: 'Manken artık sana vuracak. Vuruş gelirken sıyrılma tuşuna bas: kısa bir süre sana hiçbir şey dokunmaz. {Yonla} verdiğin yöne kayarsın.',
    goal: 'Gelen 3 vuruştan sıyrıl.',
    keys: { kb: ['C', 'Shift'], pad: ['R1'] }, need: 3, dummy: 'attacks',
    hint: 'Çok erken basma. Manken kolunu kaldırınca bas.',
    note: 'Arka arkaya sıyrılırsan koruma süresi kısalır. Bir sıyrılıp bir vurmak en iyisi.',
  },
  {
    id: 'blok', title: 'Blok',
    text: 'Blok tuşunu basılı tutarsan kalkan gibi korunursun. Vuruşlar canını yakmaz, sadece seni geri iter. Ama arkandan gelen vuruşu göremezsin.',
    goal: '3 vuruşu blokla.',
    keys: { kb: ['Q'], pad: ['L1'] }, need: 3, dummy: 'attacks',
    hint: 'Tuşu basılı tut ve mankene dönük dur.',
    note: 'Blok sonsuz değil: çok vuruş yersen koruman kırılır ve bir süre sersem kalırsın.',
  },
  {
    id: 'parry', title: 'Savuşturma',
    text: 'Şimdi zor olanı. Blok tuşuna basıp hemen bırakırsan savuşturursun. Tam zamanında yaparsan manken sersemler ve sıra sende olur.',
    goal: '2 vuruşu savuştur.',
    keys: { kb: ['Q'], pad: ['L1'] }, need: 2, dummy: 'attacks',
    hint: 'Basılı tutma — dokun ve bırak. Vuruş sana değmeden hemen önce yap.',
    note: 'Savuşturduktan sonraki ilk vuruşun mankeni doğrudan yere serer.',
  },
  {
    id: 'havada-yumruk', title: 'Havada Yumruk',
    text: 'Zıpladığın anda da vurabilirsin. Havadaki yumruk hafiftir ama karşındakini şaşırtır. Her zıplayışta iki hakkın var.',
    goal: 'Havadayken mankene 2 kez vur.',
    keys: { kb: ['Space', 'J'], pad: ['A', 'X'] }, need: 2, dummy: 'idle',
    hint: 'Bir adım geride dur; çok yapışırsan yumruk mankenin üstünden geçer.',
  },
  {
    id: 'ucan-tekme', title: 'Uçan Tekme',
    text: 'Havada ağır tuşa basarsan ayağını uzatıp uçarsın. Çok serttir, uzağa gönderir. Ama ıskalarsan yüzüstü düşersin — riskli.',
    goal: 'Mankene 1 kez uçan tekme yapıştır.',
    keys: { kb: ['Space', 'K'], pad: ['A', 'Y'] }, need: 1, dummy: 'idle',
    hint: 'Yerinde zıpla, yukarı çıkarken ağır tuşa bas — tekme seni ileri taşır.',
    note: 'Kenardaki birine bunu yaparsan çoğu zaman aşağı gider.',
  },
  {
    id: 'hizli-inis', title: 'Hızlı İniş',
    text: 'Havada yukarı çıkışın bitince blok tuşuna basarsan taş gibi aşağı düşersin. Havada sana vurmaya çalışanlardan böyle kurtulursun.',
    goal: '3 kez hızlı in.',
    keys: { kb: ['Space', 'Q'], pad: ['A', 'L1'] }, need: 3, dummy: 'idle',
    hint: 'Yükselirken değil, düşmeye başlayınca bas.',
  },
  {
    id: 'silah-al', title: 'Silahı Al',
    text: 'Arenaya silahlar düşer. Yanına gidip tutma tuşuna basarsan alırsın. Silahla menzilin uzar, vuruşun sertleşir.',
    goal: 'Yerdeki kılıcı al.',
    keys: { kb: ['L', 'E'], pad: ['B'] }, need: 1, dummy: 'idle',
    hint: 'Kılıcın üstüne bas ve tutma tuşuna dokun.',
    note: 'Elin doluyken tutma yapamazsın. Silah bir süre sonra kırılır, o zaman ellerin geri gelir.',
  },
  {
    id: 'silahla-vur', title: 'Silahla Vur',
    text: 'Silahlıyken tuşlar aynı ama hamleler değişir. Daha uzaktan vurursun, daha çok acıtırsın. Ama biraz daha yavaşsındır.',
    goal: 'Silahla 4 kez vur.',
    keys: { kb: ['J', 'K'], pad: ['X', 'Y'] }, need: 4, dummy: 'idle',
    hint: 'Uzaklığa aldanma — silahın ucu daha uzağa yetişir.',
  },
  {
    id: 'silah-firlat', title: 'Silahı Fırlat',
    text: 'Silahını fırlatabilirsin de. Uzaktaki birine yetişmenin en kolay yolu bu. Attıktan sonra ellerin boş kalır.',
    goal: 'Önce kılıcı at (1), sonra isabet ettir (2).',
    keys: { kb: ['L', 'E'], pad: ['B'] }, need: 2, dummy: 'idle',
    hint: 'Mankene dön, sonra tutma tuşuna bas — silah o yöne uçar.',
    note: 'Yere düşen silahı herkes alabilir. Attığın silah sana geri dönebilir.',
  },
  {
    id: 'kenara-at', title: 'Kenara At',
    text: 'Bu oyunun tek amacı bu: karşındakini platformdan çıkarmak. Mankeni kenara doğru sür, sonra sert bir vuruşla ya da fırlatarak aşağı gönder.',
    goal: 'Mankeni platformdan çıkar.',
    keys: { kb: ['K', 'L'], pad: ['Y', 'B'] }, need: 1, dummy: 'idle',
    hint: 'Önce sayısını büyüt. Yüzdesi yükseldikçe daha kolay uçar.',
    note: 'Yerde yatan birini de tekmeyle kenardan kaydırabilirsin.',
  },
  {
    id: 'taunt', title: 'Gösteriş',
    text: 'Bir de gösteriş var. İşe yaramaz gibi görünür ama kendi sersemliğini hızla söndürür. Yalnız güvendeyken yap.',
    goal: '1 kez gösteriş yap.',
    keys: { kb: ['T'], pad: ['D-pad ↓'] }, need: 1, dummy: 'idle',
    hint: 'T tuşuna bas.',
    note: 'Gösteriş yaparken vurulursan yarıda kesilir.',
  },
  {
    id: 'serbest-antrenman', title: 'Serbest Antrenman',
    text: 'Artık her şeyi biliyorsun. Manken bundan sonra karşılık verecek — ama acelen yok, istediğin kadar kal. Hazır olduğunda menüye dön ve gerçek maça gir.',
    goal: '',
    keys: { kb: [], pad: [] }, need: 0, dummy: 'fights',
    hint: 'İstediğin dersi B ile geri alabilirsin.',
  },
];

export interface TutorialOptions {
  playerId: number;
  dummyId: number;
  /** Ses/panel işaretleri (view katmanı bağlar); ders mantığı ses bilmez. */
  onCue?: (cue: TutorialCue) => void;
  /** Testler için ders listesi değiştirilebilir; normalde LESSONS. */
  lessons?: readonly TutorialLesson[];
}

/** Ders durumu: 'active' hedef bekleniyor, 'done' hedef doldu (geçiş gecikmesi işliyor). */
export type TutorialState = 'active' | 'done';

/**
 * Ders makinesi. Presenter her tick `onTick(world)`, her olay `onEvent(e, world)` çağırır.
 * NEDEN iki kanal: bazı dersler olayla (zıplama, vuruş), bazıları dünya durumuyla (hız, yüzde, sersemlik halkası) ölçülür.
 */
export class TutorialRunner {
  readonly lessons: readonly TutorialLesson[];
  /** Sayaç: panel "done/need" gösterir. NEDEN yerinde güncellenen tek nesne: panel her karede okur, tahsis olmasın. */
  readonly progress = { done: 0, need: 0 };
  index = 0;
  state: TutorialState = 'active';
  /** Dersin başladığı tick ve son ilerleme tick'i (ipucu zamanlaması bundan). */
  startTick = 0;
  lastProgressTick = 0;
  /** Hedef dolduğu tick; NO_TICK → geçiş beklemiyor. */
  doneTick = NO_TICK;

  private readonly playerId: number;
  private readonly dummyId: number;
  private readonly cueFn: ((cue: TutorialCue) => void) | undefined;
  private lastTick = 0;
  /** Sıralı derslerde (ayakta-tut, silah-firlat) hangi adımdayız. */
  private step = 0;
  /** 1. ders: kesintisiz yürüme/koşma sayacı. */
  private holdTicks = 0;
  /** 8. ders: yükselen kenar için önceki sersemlik; −1 = daha örneklenmedi. */
  private prevStun = -1;
  /** 9. ders yedek koşulu: oyuncunun son isabet tick'i. */
  private lastPlayerHitTick = NO_TICK;
  /** 13. ders: kuklanın son hamle başlangıcı — sıyrılma yalnız bu pencerede sayılır (ders zamanlamayı öğretiyor). */
  private lastDummyMoveTick = NO_TICK;
  /** Övgü döngüsü: tamamlanan her derste bir sonraki övgüye geçilir. */
  private praiseIdx = 0;
  private praiseNext = 0;

  constructor(opts: TutorialOptions) {
    this.lessons = opts.lessons ?? LESSONS;
    this.playerId = opts.playerId;
    this.dummyId = opts.dummyId;
    this.cueFn = opts.onCue;
    this.progress.need = this.lesson.need;
  }

  get lesson(): TutorialLesson {
    const l = this.lessons[this.index];
    if (!l) throw new Error('tutorial lesson ' + this.index);
    return l;
  }
  /** Bu dersteki kukla davranışı (denetleyici bunu okur). */
  get dummyMode(): DummyMode { return this.lesson.dummy; }
  /** Panel başlığı için: "7 / 24". */
  get lessonNo(): number { return this.index + 1; }
  get total(): number { return this.lessons.length; }
  get isLast(): boolean { return this.index === this.lessons.length - 1; }
  /** 'done' iken panelde açıklamanın yerine geçen kısa övgü. */
  get praise(): string { return PRAISE[this.praiseIdx] ?? PRAISE[0]; }

  // ───────────────────────────── Gezinme ─────────────────────────────
  /** Sonraki ders (N / R2). Son dersteyse bir şey yapmaz. */
  next(tick = this.lastTick): void { if (!this.isLast) this.goTo(this.index + 1, tick, 'advance'); }
  /** Önceki ders (B / L2). Atlanan ders kilitlenmez. */
  prev(tick = this.lastTick): void { if (this.index > 0) this.goTo(this.index - 1, tick, 'back'); }
  /** Atla (N): sonrakiyle aynı ama sesi farklı (TUTORIAL §5). */
  skip(tick = this.lastTick): void { if (!this.isLast) this.goTo(this.index + 1, tick, 'skip'); }
  /** Dersi baştan al (R). */
  restart(tick = this.lastTick): void { this.goTo(this.index, tick, 'back'); }
  /** Belirli derse git (menü/derin bağlantı). */
  goTo(i: number, tick = this.lastTick, cue: TutorialCue | null = null): void {
    if (i < 0 || i >= this.lessons.length) return;
    this.index = i;
    this.resetProgress(tick);
    if (cue) this.cue(cue);
  }

  /** Sayaç sıfırlama (ders değişimi, R, oyuncunun düşüp geri gelmesi). */
  resetProgress(tick = this.lastTick): void {
    this.progress.done = 0;
    this.progress.need = this.lesson.need;
    this.state = 'active';
    this.doneTick = NO_TICK;
    this.startTick = tick;
    this.lastProgressTick = tick;
    this.step = 0;
    this.holdTicks = 0;
    this.prevStun = -1;
    this.lastPlayerHitTick = NO_TICK;
    this.lastDummyMoveTick = NO_TICK;
  }

  // ───────────────────────────── İpucu ─────────────────────────────
  /** Son ilerlemeden bu yana beklenen süre geçtiyse ipucu satırı görünür ve ders boyunca kalır (TUTORIAL §5). */
  hintDue(nowTick: number): boolean {
    if (this.state !== 'active' || this.lesson.hint === '') return false;
    const delay = this.lesson.need > 0 ? TUT.hintDelay : TUT.idleHintDelay;
    return nowTick - this.lastProgressTick >= delay;
  }
  /** İpucunun bir kez daha yanıp sönme anı (tek tick'lik kenar). */
  hintPulse(nowTick: number): boolean {
    if (this.state !== 'active' || this.lesson.need <= 0) return false;
    return nowTick - this.lastProgressTick === TUT.hintPulseDelay;
  }

  // ───────────────────────────── Kanallar ─────────────────────────────
  /**
   * Sim olayı. Yanlış hamlenin cezası yoktur: eşleşmeyen olay sessizce yok sayılır (TUTORIAL §5).
   */
  onEvent(e: SimEvent, world: SimWorld): void {
    this.lastTick = e.tick;
    // 9. dersin yedek koşulu için oyuncunun son isabeti (lastHitBy silinmiş olabilir).
    if (e.type === Ev.Hit && e.actor === this.playerId) this.lastPlayerHitTick = e.tick;
    // 13. dersin penceresi için kuklanın hamle başlangıcı (boşa basılan sıyrılma sayılmasın).
    if (e.type === Ev.MoveStart && e.actor === this.dummyId) this.lastDummyMoveTick = e.tick;
    // Oyuncu düşüp geri geldiyse sayaç sıfırlanır — düşmek "başarısızlık" değil ama ders baştan sayılır.
    if (e.type === Ev.Respawn && e.actor === this.playerId) { this.resetProgress(e.tick); return; }
    if (this.state !== 'active' || this.lesson.need <= 0) return;
    if (this.matches(e, world)) this.gain(e.tick);
  }

  /**
   * Her sim tick'i. Dünya durumuyla ölçülen dersleri ilerletir ve tamamlanan dersin geçişini yapar.
   */
  onTick(world: SimWorld): void {
    const tick = world.tick;
    this.lastTick = tick;
    if (this.state === 'active') { this.checkWorld(world, tick); return; }
    if (this.doneTick === NO_TICK) return;
    // Oyuncu havadaysa / hamlenin içindeyse / tutuyorsa geçiş bekler (TUTORIAL §1).
    if (tick - this.doneTick < TUT.advanceDelay || !this.settled(world)) return;
    if (this.isLast) { this.doneTick = NO_TICK; return; }
    this.goTo(this.index + 1, tick, 'advance');
  }

  // ───────────────────────────── İç mantık ─────────────────────────────
  /** Olay bu dersi bir adım ilerletir mi (TUTORIAL §6 tablosu). */
  private matches(e: SimEvent, world: SimWorld): boolean {
    const p = this.playerId, d = this.dummyId;
    switch (this.lesson.id) {
      case 'zipla': return e.type === Ev.Jump && e.actor === p;
      case 'cift-zipla': return e.type === Ev.DoubleJump && e.actor === p;
      case 'ilk-yumruk': return this.playerHit(e, world, MOVE_ID.L1);
      case 'uclu-seri': return this.playerHit(e, world, MOVE_ID.L3);
      case 'tekme': return this.playerHit(e, world, MOVE_ID.H1);
      case 'yere-serme': return e.type === Ev.Knockdown && e.actor === d && this.byPlayer(world, e.tick);
      // Omuza alma İKİ yoldan olur ve olayları FARKLI: AYAKTAKİNİ tutup bekleyince `Ev.PickUpFighter`, YATANI kaldırınca
      // (G3_pickup) `Ev.Grab`. İkisi de omuza binince `Ev.Carry` üretir → ders "omzuna al" dediği için ölçü Carry.
      // NEDEN değişti: kukla artık bu derse YATARAK başlıyor (tutorialMode.upkeepLying), yani PickUpFighter hiç gelmiyordu
      // ve ders 0/2'de kilitleniyordu (oyun testi: 11 başarılı kaldırma, sayaç 0).
      case 'yerden-kaldir': return e.type === Ev.Carry && e.actor === p;
      case 'tasi-ve-firlat': return e.type === Ev.Throw && e.actor === p;
      // Sırayla: önce tutuş, sonra fırlatma. İlk adım tekrarlanırsa sayaç 1'de kalır (TUTORIAL §6).
      case 'ayakta-tut':
        if (this.step === 0) { if (e.type === Ev.Hold && e.actor === p) { this.step = 1; return true; } return false; }
        return e.type === Ev.Throw && e.actor === p;
      // Sıyrılma yalnız kukla vururken sayılır: ders zamanlamayı öğretiyor, tuşa rastgele basmayı değil.
      case 'dodge': return e.type === Ev.Dodge && e.actor === p && this.inDummyAttack(e.tick);
      // Blocked/Parry olaylarında actor = VURAN, target = savunan (sim/block.ts, sim/hits.ts) → ikisinde de target = oyuncu.
      case 'blok': return e.type === Ev.Blocked && e.target === p;
      case 'parry': return e.type === Ev.Parry && e.target === p;
      case 'havada-yumruk': return this.playerHit(e, world, MOVE_ID.A1);
      case 'ucan-tekme': return this.playerHit(e, world, MOVE_ID.A2);
      case 'hizli-inis': return e.type === Ev.FastFall && e.actor === p;
      case 'silah-al': return e.type === Ev.ItemPickup && e.actor === p;
      case 'silahla-vur': return this.playerWeaponHit(e, world);
      case 'silah-firlat':
        if (this.step === 0) { if (e.type === Ev.ItemThrow && e.actor === p) { this.step = 1; return true; } return false; }
        return e.type === Ev.ItemHit && e.target === d;
      case 'kenara-at': return e.type === Ev.Fell && e.actor === d && e.target === p;
      case 'taunt': return e.type === Ev.Taunt && e.actor === p;
      default: return false;   // dünya durumuyla ölçülen dersler (yuru-kos, hasar-yuzdesi, sersemlik-halkasi) ve serbest antrenman
    }
  }

  /** Dünya durumuyla ölçülen dersler. */
  private checkWorld(world: SimWorld, tick: number): void {
    const id = this.lesson.id;
    if (id === 'yuru-kos') {
      const p = world.fighters[this.playerId];
      if (!p) return;
      const speed = Math.hypot(p.vel.x, p.vel.z);
      // 0: bir süre yürü (> 0.3) · 1: bir süre koş (≥ runThreshold). Sayaç kesintide sıfırlanır (aralıklı basış "yürüdüm" saymasın).
      const ok = this.progress.done === 0 ? speed > TUT.walkSpeed : speed >= T.MOVE.runThreshold;
      this.holdTicks = ok ? this.holdTicks + 1 : 0;
      if (this.holdTicks >= TUT.walkTicks) { this.holdTicks = 0; this.gain(tick); }
      return;
    }
    if (id === 'hasar-yuzdesi') {
      const dm = world.fighters[this.dummyId];
      if (!dm) return;
      // Sayaç yüzdenin kendisi ("18/40"). Geri gitmez: kukla kendini tazelese de öğrenci ilerlemesini kaybetmez.
      this.setDone(Math.min(TUT.damageGoal, Math.floor(dm.damage)), tick);
      return;
    }
    if (id === 'sersemlik-halkasi') {
      const dm = world.fighters[this.dummyId];
      if (!dm) return;
      const th = T.COMBAT.heavyKnockdownStun;   // "!" işaretinin eşiği (HUD §14)
      const cur = dm.stun;
      if (this.prevStun >= 0 && this.prevStun < th && cur >= th) this.gain(tick);
      this.prevStun = cur;
      return;
    }
    if (this.lesson.need <= 0) {
      // Serbest antrenman: ipucu yalnız gerçekten hareketsiz kalınca gelsin.
      const p = world.fighters[this.playerId];
      if (p && Math.hypot(p.vel.x, p.vel.z) > TUT.walkSpeed) this.lastProgressTick = tick;
    }
  }

  /** `Ev.Hit`, vuran oyuncu ve hamle id'si eşleşiyor mu. */
  private playerHit(e: SimEvent, world: SimWorld, moveId: string): boolean {
    if (e.type !== Ev.Hit || e.actor !== this.playerId) return false;
    return world.moves[e.move]?.id === moveId;
  }
  /** Silahlı herhangi bir isabet (TUTORIAL §6: `weapon !== 'unarmed'`). */
  private playerWeaponHit(e: SimEvent, world: SimWorld): boolean {
    if (e.type !== Ev.Hit || e.actor !== this.playerId) return false;
    const m = world.moves[e.move];
    return m !== undefined && m.weapon !== 'unarmed';
  }
  /** Kuklayı yere seren oyuncu muydu: lastHitBy, yoksa son 60 t içinde oyuncunun isabeti. */
  private byPlayer(world: SimWorld, tick: number): boolean {
    const dm = world.fighters[this.dummyId];
    if (dm && dm.lastHitBy === this.playerId) return true;
    return this.lastPlayerHitTick !== NO_TICK && tick - this.lastPlayerHitTick <= TUT.hitWindow;
  }

  /** Kuklanın hamlesi hâlâ "geliyor" mu: son MoveStart'ından bu yana 30 t geçmediyse evet (9. dersteki pencere deseni). */
  private inDummyAttack(tick: number): boolean {
    return this.lastDummyMoveTick !== NO_TICK && tick - this.lastDummyMoveTick <= TUT.dodgeWindow;
  }

  /** Oyuncu geçiş için sakin mi (yerde, hamlesiz, kimseyi tutmuyor). */
  private settled(world: SimWorld): boolean {
    const p = world.fighters[this.playerId];
    if (!p) return true;
    if (!p.grounded || p.moveIdx >= 0 || p.grabTarget !== NO_ID || p.grabbedBy !== NO_ID) return false;
    return p.state === FState.Idle || p.state === FState.Move || p.state === FState.Block;
  }

  private gain(tick: number): void { this.setDone(this.progress.done + 1, tick); }

  private setDone(value: number, tick: number): void {
    const need = this.progress.need;
    if (need <= 0 || this.state !== 'active') return;
    const v = Math.min(need, value);
    if (v <= this.progress.done) return;
    this.progress.done = v;
    this.lastProgressTick = tick;
    if (v < need) { this.cue('progress'); return; }
    this.state = 'done';
    this.doneTick = tick;
    this.praiseIdx = this.praiseNext;
    this.praiseNext = (this.praiseNext + 1) % PRAISE.length;
    this.cue('complete');
  }

  private cue(c: TutorialCue): void { this.cueFn?.(c); }
}
